import { usePerfil } from "@/contexts/autorizacao/perfil/hooks/usePerfil";
import { Perfil } from "@/contexts/autorizacao/perfil/model/Perfil";
import { usePermissao } from "@/contexts/autorizacao/permissao";
import { useUsuarioLogado } from "@/contexts/autorizacao/usuario";
import { PageLoader } from "@/shared/components/PageLoader/PageLoader";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { formatMessageError } from "@/shared/utils/formatMessageError";
import { EditOutlined, SaveOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  App,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Grid,
  List,
  Modal,
  Result,
  Row,
  Space,
  Tooltip,
  Typography,
} from "antd";
import { useEffect, useState } from "react";

const PERFIL_ADMIN_ID = 1;

interface FormValues {
  permissoesId: number[];
}

export function ListaPerfil() {
  const { isOnline } = useNetworkStatus();
  const screens = Grid.useBreakpoint();
  const {
    getPerfisApi,
    getPermissoesByPerfilIdApi,
    updatePermissoesPerfilApi,
  } = usePerfil();
  const { usuarioLogado } = useUsuarioLogado();
  const { getPermissoes } = usePermissao();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [perfilEdit, setPerfilEdit] = useState<Perfil | null>(null);
  const [form] = Form.useForm<FormValues>();
  const { notification, message } = App.useApp();

  const {
    data: perfis,
    isLoading: isLoadingPerfis,
    error: errorPerfis,
  } = useQuery({
    queryKey: ["perfis"],
    queryFn: async () => {
      const data = await getPerfisApi();
      return data;
    },
    enabled: isOnline,
  });

  const { data: todasPermissoes } = useQuery({
    queryKey: ["permissoes"],
    enabled: perfilEdit !== null,
    queryFn: async () => {
      const data = await getPermissoes();
      return data;
    },
  });

  const handleOpenModal = async (perfil: Perfil) => {
    setIsOpenModal(true);
    setPerfilEdit(perfil);
    const permissoes = await getPermissoesByPerfilIdApi(perfil.id);
    form.setFieldsValue({
      permissoesId: permissoes.map((p) => p.id),
    });
  };

  const handleCloseModal = () => {
    setIsOpenModal(false);
    setPerfilEdit(null);
  };

  const handleSubmit = async (values: FormValues) => {
    if (!perfilEdit) return;

    try {
      message.loading({
        content: "Atualizando permissões...",
        key: "submit",
        duration: 0,
      });
      const permissoesSelecionadas = todasPermissoes?.filter((p) =>
        values.permissoesId.includes(p.id)
      );

      await updatePermissoesPerfilApi(perfilEdit.id, {
        permissoes: permissoesSelecionadas || [],
      });

      message.destroy("submit");
      notification.success({
        message: "Permissões do perfil atualizadas com sucesso",
      });
      handleCloseModal();
    } catch (error) {
      message.destroy("submit");
      console.error("Erro ao atualizar permissões do perfil:", error);
      const messageError = formatMessageError(error);
      notification.error({
        message: "Não foi possível atualizar as permissões do perfil",
        description: messageError,
      });
    }
  };

  useEffect(() => {
    document.title = "Inspeçao Mobile - Gerenciamento de Perfis";
  }, []);

  if (!isOnline) {
    return (
      <Result
        status="error"
        title="Sem conexão com a internet"
        subTitle="Conecte-se à internet e tente novamente."
      />
    );
  }

  if (isLoadingPerfis) {
    return <PageLoader />;
  }

  if (errorPerfis instanceof Error) {
    return (
      <Result
        status="error"
        title="Não foi possível carregar os perfis"
        subTitle={errorPerfis.message}
      />
    );
  }

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} align="center">
        <Typography.Title
          level={screens.xs ? 4 : 3}
          style={{ textAlign: "center" }}
        >
          Gerenciamento de Perfis
        </Typography.Title>
      </Space>

      <Divider />

      <List
        size="small"
        bordered
        dataSource={perfis || []}
        renderItem={(item, ind) => {
          return (
            <List.Item
              key={ind}
              extra={
                <Space>
                  <Tooltip title="Editar" destroyOnHidden>
                    <Button
                      disabled={
                        (item.id === PERFIL_ADMIN_ID &&
                          usuarioLogado.perfil.id !== PERFIL_ADMIN_ID) ||
                        !item.ativo
                      }
                      onClick={() => handleOpenModal(item)}
                      icon={<EditOutlined />}
                    />
                  </Tooltip>
                </Space>
              }
            >
              <List.Item.Meta
                title={
                  <Typography.Text
                    strong
                    style={{
                      opacity: item.ativo ? 1 : 0.7,
                    }}
                  >
                    {item.nome} {item.ativo ? "" : "(inativo)"}
                  </Typography.Text>
                }
              />
            </List.Item>
          );
        }}
      />

      <Modal
        open={isOpenModal}
        onCancel={handleCloseModal}
        okText="Salvar"
        okButtonProps={{
          htmlType: "submit",
          type: "primary",
          form: "form",
          icon: <SaveOutlined />,
        }}
        cancelText="Cancelar"
        width={800}
        title={`Editar Permissões do Perfil ${perfilEdit?.nome}`}
        style={{
          top: 20,
        }}
      >
        <Divider />
        <Form form={form} id="form" layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="permissoesId">
            <Checkbox.Group style={{ width: "100%" }}>
              <Row gutter={[16, 16]}>
                {todasPermissoes?.map((permissao) => (
                  <Col key={permissao.id} xs={24} sm={12}>
                    <Checkbox value={permissao.id}>
                      {permissao.descricao}
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <Divider />
        </Form>
      </Modal>
    </>
  );
}
